import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faGauge,
  faClipboardList,
  faPlus,
  faUsers,
  faFileLines,
  faEnvelope,
  faChartBar,
  faBullseye,
  faUser,
  faShieldHalved,
  faGear,
  faUsersGear,
  faFolderOpen,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import LanguageSwitcher from './LanguageSwitcher';
import { useUserRoles } from '../auth';

interface NavItem {
  to: string;
  labelKey: string;
  icon: IconDefinition;
  ariaKey: string;
  roles?: string[];
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard',         labelKey: 'nav.dashboard',         icon: faGauge,          ariaKey: 'nav.dashboardAria',       roles: ['TENDER_VIEWER', 'TENDER_MANAGER', 'HR_ADMIN'] },
  { to: '/tenders',           labelKey: 'nav.tenders',           icon: faClipboardList,  ariaKey: 'nav.tendersAria',         roles: ['TENDER_VIEWER', 'TENDER_MANAGER'] },
  { to: '/tenders/submit',    labelKey: 'nav.submitTender',      icon: faPlus,           ariaKey: 'nav.submitTenderAria',    roles: ['TENDER_MANAGER'] },
  { to: '/employees',         labelKey: 'nav.employees',         icon: faUsers,          ariaKey: 'nav.employeesAria',       roles: ['TENDER_VIEWER', 'TENDER_MANAGER', 'HR_ADMIN'] },
  { to: '/project-reports',   labelKey: 'nav.projectReports',    icon: faFolderOpen,     ariaKey: 'nav.projectReportsAria',  roles: ['EMPLOYEE', 'PROJECT_ADMIN', 'HR_ADMIN'] },
  { to: '/cvs',               labelKey: 'nav.cvManagement',      icon: faFileLines,      ariaKey: 'nav.cvManagementAria',    roles: ['TENDER_MANAGER', 'HR_ADMIN'] },
  { to: '/mailbox',           labelKey: 'nav.mailbox',           icon: faEnvelope,       ariaKey: 'nav.mailboxAria',         roles: ['TENDER_MANAGER'] },
  { to: '/analytics',         labelKey: 'nav.analytics',         icon: faChartBar,       ariaKey: 'nav.analyticsAria',       roles: ['HR_ADMIN'] },
  { to: '/skill-development', labelKey: 'nav.skillDevelopment',  icon: faBullseye,       ariaKey: 'nav.skillDevelopmentAria', roles: ['HR_ADMIN'] },
  { to: '/my-team',           labelKey: 'nav.myTeam',            icon: faUsersGear,      ariaKey: 'nav.myTeamAria',          roles: ['EMPLOYEE'] },
  { to: '/my-skills',         labelKey: 'nav.mySkills',          icon: faUser,           ariaKey: 'nav.mySkillsAria',        roles: ['EMPLOYEE'] },
  { to: '/dpo',               labelKey: 'nav.dpoDashboard',      icon: faShieldHalved,   ariaKey: 'nav.dpoDashboardAria',    roles: ['DPO'] },
  { to: '/admin',             labelKey: 'nav.admin',             icon: faGear,           ariaKey: 'nav.adminAria',           roles: ['ADMIN'] },
  { to: '/admin/skills',      labelKey: 'nav.skillAdmin',        icon: faWrench,         ariaKey: 'nav.skillAdminAria',      roles: ['ADMIN', 'PROJECT_ADMIN'] },
];

export default function Layout() {
  const location = useLocation();
  const { t } = useTranslation();
  const { roles: userRoles, loaded: rolesLoaded } = useUserRoles();

  // Filter nav items by role. When auth is disabled (no roles loaded / empty roles), show all items.
  const visibleItems = NAV_ITEMS.filter((item) => {
    if (!item.roles || item.roles.length === 0) return true;
    if (!rolesLoaded || userRoles.length === 0) return true; // auth disabled — show all
    if (userRoles.includes('ADMIN')) return true;
    return item.roles.some((r) => userRoles.includes(r));
  });

  // Derive page title from pathname for the <title> element
  const activeItem = NAV_ITEMS.find((item) =>
    location.pathname === item.to ||
    (item.to !== '/dashboard' && location.pathname.startsWith(item.to))
  );
  const pageTitle = activeItem ? `${t(activeItem.labelKey)} — StaffLens` : 'StaffLens';

  // Update document title reactively
  if (typeof document !== 'undefined') {
    document.title = pageTitle;
  }

  // Soft-launch toggle state — true when currently viewing any v2/* route
  const onV2 = location.pathname.startsWith('/v2/');

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* ----------------------------------------------------------------
          Sidebar navigation
          ---------------------------------------------------------------- */}
      <nav
        aria-label={t('nav.mainNavigation')}
        className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-white border-r border-gray-200 shadow-sm z-10"
      >
        {/* Logo / branding */}
        <div className="flex h-16 items-center gap-2.5 px-6 border-b border-gray-100">
          <img src="/logo.png" alt="" className="h-8 rounded-lg object-contain" aria-hidden="true" />
          <span className="text-lg font-bold text-brand-700 tracking-tight">
            StaffLens
          </span>
        </div>

        {/* Nav links */}
        <ul className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {visibleItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                aria-label={t(item.ariaKey)}
                end={item.to === '/tenders/submit'}
                className={({ isActive }) =>
                  [
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                  ].join(' ')
                }
              >
                <FontAwesomeIcon icon={item.icon} className="w-4 h-4 shrink-0" aria-hidden="true" />
                {t(item.labelKey)}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Soft-launch UI version toggle. Tenders flow is opt-in for v2 during the transition. */}
        <div className="border-t border-gray-100 px-4 py-2.5">
          {onV2 ? (
            <Link
              to="/tenders"
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1.5 transition-colors"
            >
              <span aria-hidden="true">←</span>
              <span>Back to classic UI</span>
            </Link>
          ) : (
            <Link
              to="/v2/tenders"
              className="text-xs text-brand-600 hover:text-brand-800 flex items-center gap-1.5 font-medium transition-colors"
            >
              <span>Try the new UI</span>
              <span aria-hidden="true">→</span>
            </Link>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-between">
          <p className="text-xs text-gray-400">{t('common.version')}</p>
          <LanguageSwitcher />
        </div>
      </nav>

      {/* ----------------------------------------------------------------
          Mobile top bar
          ---------------------------------------------------------------- */}
      <header className="md:hidden fixed top-0 inset-x-0 z-20 flex h-14 items-center justify-between bg-white border-b border-gray-200 px-4 shadow-sm">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="" className="h-7 rounded-md object-contain" aria-hidden="true" />
          <span className="text-base font-bold text-brand-700">StaffLens</span>
        </div>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {/* Mobile nav could be a hamburger; simplified here */}
          <nav aria-label={t('nav.mobileNavigation')} className="flex gap-3">
            {visibleItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                aria-label={t(item.ariaKey)}
                end={item.to === '/tenders/submit'}
                className={({ isActive }) =>
                  [
                    'text-lg',
                    isActive ? 'text-brand-600' : 'text-gray-500',
                  ].join(' ')
                }
              >
                <FontAwesomeIcon icon={item.icon} aria-hidden="true" />
              </NavLink>
            ))}
          </nav>
        </div>
      </header>

      {/* ----------------------------------------------------------------
          Main content area
          ---------------------------------------------------------------- */}
      <div className="flex flex-col flex-1 md:pl-64">
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 px-4 py-6 md:px-8 md:py-8 mt-14 md:mt-0 focus:outline-none"
        >
          <Outlet />
        </main>

        <footer className="border-t border-gray-100 px-6 py-3 text-xs text-gray-400 text-center">
          {t('common.copyright', { year: new Date().getFullYear() })}
        </footer>
      </div>
    </div>
  );
}
